import {
  WixPatternsContainer,
  DataExtensionState,
  ExtendedFields,
  Fqdn,
  addResizeObserver,
  fqdnToString,
  TaskState,
  WixPatternsError,
} from '@wix/bex-core';
import { SidePanelsState } from './SidePanelsState';
import { action, computed, makeObservable, observable, reaction } from 'mobx';
import type * as CustomFieldsWidgetLazyContent from '../components/CustomFieldsWidget/CustomFieldsWidgetLazyContent';
import {
  cairoErrorInLoadingAComponent,
  cairoTryAgainClicked,
  loadEnd,
  loadStart,
} from '@wix/bex-core/bi';
import { CustomFieldsFormState } from './CustomFieldsFormState';

export interface CustomFieldsWidgetStateParams {
  readonly container: WixPatternsContainer;
  readonly fqdn: Fqdn;
}

export type CustomFieldsWidgetSize = 'default' | 'small';

export class CustomFieldsWidgetState {
  readonly container;
  readonly dataExtension;
  readonly initTask = new TaskState();

  formState: CustomFieldsFormState | null = null;

  _size: CustomFieldsWidgetSize;
  _widgetContentRef: { current: HTMLDivElement | null } = {
    current: null,
  };

  readonly panels;
  readonly reportBi;
  readonly fqdn;

  _customFieldsLazyComponents:
    | typeof CustomFieldsWidgetLazyContent
    | undefined = undefined;

  constructor({ container, fqdn }: CustomFieldsWidgetStateParams) {
    this.container = container;

    this.fqdn = fqdn;
    this._size = 'default';

    this.panels = new SidePanelsState({
      panelGroupName: fqdnToString(fqdn),
      container,
    });

    this.reportBi = container.createBILogger({
      componentType: 'CustomFieldsWidget',
      ...fqdn,
    });

    this.dataExtension = new DataExtensionState({
      container,
      reportBi: this.reportBi,
      fqdn,
      closeSidePanel: this.panels.closeSidePanel,
    });

    makeObservable(this, {
      hasWritePermissions: computed,
      widgetSize: computed,
      setWidgetContentRef: action,
      isLoading: computed,
      isError: computed,
      _size: observable.ref,
      _widgetContentRef: observable.ref,
      _adjustWidgetSize: action.bound,
      formState: observable.ref,
      retry: action.bound,
    });
  }

  _appLoadingStart() {
    const startTime = performance.now();
    const commonBiParams = this._commonDynamicBiParams();

    this.reportBi(
      loadStart({
        ...commonBiParams,
        url: window.location.href,
      }),
    );

    return () => {
      this.reportBi(
        loadEnd({
          ...commonBiParams,
          url: window.location.href,
          loadingTime: Math.round(performance.now() - startTime),
        }),
      );
    };
  }

  init({ appLoaded }: { appLoaded?: () => void }) {
    const disposers = [
      this.dataExtension.init(),
      reaction(
        () => this.dataExtension.availableUserCustomFields,
        (fields) => {
          this.formState?.setNewSchema(fields);
        },
        {
          fireImmediately: true,
        },
      ),
    ];

    const { initTask, dataExtension, container } = this;

    initTask.runOnce(async () => {
      dataExtension.initTask.runOnce();

      const [lazyComponents] = await Promise.all([
        import(
          /* webpackChunkName: "CustomFieldsWidgetLazyContent" */
          '../components/CustomFieldsWidget/CustomFieldsWidgetLazyContent'
        ).catch((err) => {
          throw new WixPatternsError({
            errorCode: 'LoadCustomFieldsWidgetLazyContentFailed',
            originalException: err,
          });
        }),
        dataExtension.initTask.status.promise?.catch((err) => {
          throw new WixPatternsError({
            errorCode: 'InitCustomFieldsDataExtensionFailed',
            originalException: err,
          });
        }),
        container.initTask.status.promise?.catch((err) => {
          throw new WixPatternsError({
            errorCode: 'InitCustomFieldsWidgetContainerFailed',
            originalException: err,
          });
        }),
      ]).catch((error) => {
        this.container.internalMonitor.reportError({
          error,
        });

        this.reportBi(
          cairoErrorInLoadingAComponent({
            errorType:
              error instanceof WixPatternsError ? error.name : undefined,
          }),
        );

        throw error;
      });

      this._customFieldsLazyComponents = lazyComponents;

      appLoaded?.();
    });

    return () => {
      disposers.forEach((d) => d?.());
    };
  }

  initContent() {
    const disposers = [
      addResizeObserver(
        this._widgetContentRef.current,
        this._adjustWidgetSize.bind(this),
      ),
    ];

    this._adjustWidgetSize();

    return () => {
      disposers.forEach((d) => d?.());
    };
  }

  _commonDynamicBiParams() {
    return {
      url: window.location.href,
    };
  }

  retry() {
    this.reportBi(
      cairoTryAgainClicked({
        actionName: 'Load custom fields',
        loaction: 'Custom fields widget',
      }),
    );

    this.initTask.runOnce();
  }

  get isDirty() {
    return !!this.formState?.isDirty;
  }

  get widgetSize() {
    return this._size;
  }

  get isLoading() {
    const { status } = this.initTask;
    return status.isIdle || status.isLoading;
  }

  get ActionButtonsComponent() {
    return this._customFieldsLazyComponents?.ActionButtons;
  }

  get CustomFieldsWidgetContent() {
    return this._customFieldsLazyComponents?.CustomFieldsWidgetContent;
  }

  setWidgetContentRef(ref: HTMLDivElement | null) {
    this._widgetContentRef.current = ref;
  }

  _adjustWidgetSize() {
    if (this._widgetContentRef.current) {
      const element = this._widgetContentRef.current;

      if (element.clientWidth < 842) {
        this._size = 'small';
      } else {
        this._size = 'default';
      }
    }
  }

  get isError() {
    return this.initTask.status.isError;
  }

  async validate(): Promise<{
    isValid: boolean;
    extendedFields?: ExtendedFields;
    values?: ExtendedFields;
  }> {
    const data = this.formState?.validate();

    if (!data) {
      return {
        isValid: true,
      };
    }

    if (data?.isValid) {
      const extendedFields = {
        namespaces: {
          _user_fields: data.extendedFields,
        },
      };
      return {
        isValid: true,
        extendedFields,
        values: extendedFields,
      };
    }

    return { isValid: false };
  }

  get hasWritePermissions() {
    return this.dataExtension.hasWritePermissions;
  }
}
